/**
 * Tracks playback events for TTFA (Time to First Audio) and WLH (Weekly Listening Hours)
 */

import mixpanelService from '../mixpanel-service'
import { generateUUID } from '@utils/uuid'
import { saveJournal, loadJournal, clearJournal } from './playback-journal'
import {
    EVENTS,
    PROPS,
    CONTENT_TYPES,
    PLAYER_STATES,
    ATTRIBUTION_SOURCES,
    ENDED_REASONS,
    HEARTBEAT_INTERVAL_SECONDS,
    JOURNAL_PERSIST_THROTTLE_MS
} from './analytics-spec'

const BEACON = { transport: 'sendBeacon', send_immediately: true }

class PlaybackTracker {
    constructor() {
        if (PlaybackTracker.instance) {
            return PlaybackTracker.instance
        }
        PlaybackTracker.instance = this

        this._session = null
        this._lastStateChangeTime = null
        this._lastJournalWriteAt = 0
    }

    /**
     * Start a new playback session - ends existing session if active
     */
    startSession({ contentType, contentId, sourceScreen, attributionSource, radioChannelName }) {
        if (this._session?.audioStartedFired) {
            this.endSession(ENDED_REASONS.SWITCH_CONTENT)
        }

        const requestTimestamp = performance.now()

        this._session = {
            playbackRequestId: generateUUID(),
            contentType,
            contentId,
            sourceScreen,
            attributionSource: attributionSource || ATTRIBUTION_SOURCES.UNKNOWN,
            radioChannelName,
            requestTimestamp,
            audioStartedFired: false,
            ended: false,
            playerState: null,
            isBackground: false,
            accumulatedListeningMs: 0,
            lastHeartbeatMs: 0,
            listeningSecondsCumulative: 0,
            playheadSec: null
        }

        this._lastStateChangeTime = requestTimestamp

        const props = {
            [PROPS.PLAYBACK_REQUEST_ID]: this._session.playbackRequestId,
            [PROPS.CONTENT_TYPE]: contentType,
            [PROPS.CONTENT_ID]: contentId,
            [PROPS.SOURCE_SCREEN]: sourceScreen,
            [PROPS.ATTRIBUTION_SOURCE]: this._session.attributionSource
        }

        if (radioChannelName) {
            props[PROPS.RADIO_CHANNEL_NAME] = radioChannelName
        }

        mixpanelService.track(EVENTS.PLAYBACK_REQUESTED, props)

        this._lastJournalWriteAt = 0
        this._persistJournal(true)
    }

    /**
     * Mark that audio has started playing - fires audio_started with TTFA
     */
    onAudioStarted() {
        if (!this._session || this._session.audioStartedFired) return

        this._session.audioStartedFired = true
        const ttAudioStartedMs = Math.round(performance.now() - this._session.requestTimestamp)

        mixpanelService.track(EVENTS.AUDIO_STARTED, {
            [PROPS.PLAYBACK_REQUEST_ID]: this._session.playbackRequestId,
            [PROPS.CONTENT_TYPE]: this._session.contentType,
            [PROPS.CONTENT_ID]: this._session.contentId,
            [PROPS.TT_AUDIO_STARTED_MS]: ttAudioStartedMs,
            [PROPS.ATTRIBUTION_SOURCE]: this._session.attributionSource
        })
    }

    /**
     * Update player state - accumulates listening time when state is 'playing'
     */
    updatePlayerState(state) {
        if (!this._session) return

        const now = performance.now()

        // Accumulate time if previous state was playing
        if (this._session.playerState === PLAYER_STATES.PLAYING) {
            this._session.accumulatedListeningMs += now - this._lastStateChangeTime
        }

        this._session.playerState = state
        this._lastStateChangeTime = now

        this._checkHeartbeatThreshold()
        this._persistJournal()
    }

    /**
     * Update playhead position (for on-demand content)
     */
    updatePlayhead(seconds) {
        if (this._session) {
            this._session.playheadSec = seconds
        }
    }

    _checkHeartbeatThreshold() {
        if (!this._session) return

        const thresholdMs = HEARTBEAT_INTERVAL_SECONDS * 1000
        const listeningMsSinceLastHeartbeat = this._session.accumulatedListeningMs - this._session.lastHeartbeatMs

        if (listeningMsSinceLastHeartbeat >= thresholdMs) {
            this._emitHeartbeat()
        }
    }

    _persistJournal(force = false) {
        if (!this._session) return
        const now = performance.now()
        if (!force && now - this._lastJournalWriteAt < JOURNAL_PERSIST_THROTTLE_MS) return
        this._lastJournalWriteAt = now
        saveJournal({
            playbackRequestId: this._session.playbackRequestId,
            contentType: this._session.contentType,
            contentId: this._session.contentId,
            attributionSource: this._session.attributionSource,
            radioChannelName: this._session.radioChannelName || null,
            accumulatedListeningMs: this._session.accumulatedListeningMs,
            lastHeartbeatMs: this._session.lastHeartbeatMs,
            listeningSecondsCumulative: this._session.listeningSecondsCumulative,
            playheadSec: this._session.playheadSec
        })
    }

    _emitHeartbeat(options = null) {
        if (!this._session) return

        const deltaListeningSeconds = Math.round(
            (this._session.accumulatedListeningMs - this._session.lastHeartbeatMs) / 1000
        )

        if (deltaListeningSeconds <= 0) return

        // At-most-once: advance the reported-watermark and persist it BEFORE handing the
        // event to the SDK, so a crash in the gap loses one small delta rather than
        // double-counting it on recovery (epic prefers removing over-count).
        this._session.listeningSecondsCumulative += deltaListeningSeconds
        this._session.lastHeartbeatMs = this._session.accumulatedListeningMs
        this._persistJournal(true)

        mixpanelService.track(EVENTS.PLAYBACK_HEARTBEAT, {
            [PROPS.PLAYBACK_REQUEST_ID]: this._session.playbackRequestId,
            [PROPS.CONTENT_TYPE]: this._session.contentType,
            [PROPS.CONTENT_ID]: this._session.contentId,
            [PROPS.DELTA_LISTENING_SECONDS]: deltaListeningSeconds,
            [PROPS.LISTENING_SECONDS_CUMULATIVE]: this._session.listeningSecondsCumulative,
            [PROPS.PLAYHEAD_SEC]: this._session.contentType === CONTENT_TYPES.RADIO ? null : this._session.playheadSec,
            [PROPS.IS_BACKGROUND]: this._session.isBackground,
            [PROPS.PLAYER_STATE]: this._session.playerState || PLAYER_STATES.PLAYING,
            [PROPS.ATTRIBUTION_SOURCE]: this._session.attributionSource
        }, options)
    }

    /**
     * End the current playback session
     */
    endSession(endedReason, options = null) {
        if (!this._session || this._session.ended) return

        // Flush the final partial interval so the session reports its FULL listening,
        // not just up to the last periodic heartbeat (makes short sessions accurate).
        this._accumulateCurrentTime()
        this._emitHeartbeat(options)

        this._session.ended = true
        mixpanelService.track(EVENTS.PLAYBACK_ENDED, {
            [PROPS.PLAYBACK_REQUEST_ID]: this._session.playbackRequestId,
            [PROPS.CONTENT_TYPE]: this._session.contentType,
            [PROPS.CONTENT_ID]: this._session.contentId,
            [PROPS.ENDED_REASON]: endedReason,
            [PROPS.LISTENING_SECONDS_CUMULATIVE]: this._session.listeningSecondsCumulative,
            [PROPS.ATTRIBUTION_SOURCE]: this._session.attributionSource
        }, options)

        clearJournal()
        this._session = null
    }

    /**
     * Handle visibility change (tab hidden/visible) - beacon-flushes but keeps session alive
     */
    onVisibilityChange(isHidden) {
        if (!this._session) return
        if (isHidden && this._session.playerState === PLAYER_STATES.PLAYING) this._accumulateCurrentTime()
        this._session.isBackground = isHidden
        if (isHidden) this._emitHeartbeat(BEACON)
    }

    /**
     * Handle pagehide event - ends session via sendBeacon (endSession handles the tail-flush)
     */
    onPageHide() {
        if (!this._session) return
        this.endSession(ENDED_REASONS.APP_BACKGROUND, BEACON)
    }

    /**
     * Wire the page-lifecycle listeners that report tab hide/discard - a no-op
     * in a server context, where there is no document/window
     */
    registerLifecycleListeners() {
        if (typeof document === 'undefined' || typeof window === 'undefined') return
        document.addEventListener('visibilitychange', () => this.onVisibilityChange(document.hidden))
        window.addEventListener('pagehide', () => this.onPageHide())
        // Page Lifecycle: a tab about to be discarded/frozen — beacon-flush but
        // keep the session so a later `resume` continues counting; if discarded,
        // boot recovery picks up the rest.
        document.addEventListener('freeze', () => this.onVisibilityChange(true))
    }

    /**
     * Recover a journaled session from a previous crash - emits recovery heartbeat + ended
     */
    recoverJournaledSession() {
        const j = loadJournal()
        if (!j) return

        // KNOWN LIMITATION (multi-tab): single shared localStorage key, no ownership check.
        // A second tab booting mid-session in another tab recovers this live journal and
        // double-counts — incl. the common "open a link in a new tab while playing" case.
        // Accepted + documented (see Edge cases); monitor playback_ended(recovered) rate.
        const leftoverSeconds = Math.round(((j.accumulatedListeningMs || 0) - (j.lastHeartbeatMs || 0)) / 1000)
        if (leftoverSeconds > 0) {
            const cumulative = (j.listeningSecondsCumulative || 0) + leftoverSeconds
            // Radio has no meaningful playhead; for on-demand keep a numeric value (incl. 0) or fall back to null.
            let playhead = null
            if (j.contentType !== CONTENT_TYPES.RADIO && typeof j.playheadSec === 'number') {
                playhead = j.playheadSec
            }
            mixpanelService.track(EVENTS.PLAYBACK_HEARTBEAT, {
                [PROPS.PLAYBACK_REQUEST_ID]: j.playbackRequestId,
                [PROPS.CONTENT_TYPE]: j.contentType,
                [PROPS.CONTENT_ID]: j.contentId,
                [PROPS.DELTA_LISTENING_SECONDS]: leftoverSeconds,
                [PROPS.LISTENING_SECONDS_CUMULATIVE]: cumulative,
                [PROPS.PLAYHEAD_SEC]: playhead,
                [PROPS.IS_BACKGROUND]: true,
                [PROPS.PLAYER_STATE]: PLAYER_STATES.PLAYING,
                [PROPS.ATTRIBUTION_SOURCE]: j.attributionSource
            })
            mixpanelService.track(EVENTS.PLAYBACK_ENDED, {
                [PROPS.PLAYBACK_REQUEST_ID]: j.playbackRequestId,
                [PROPS.CONTENT_TYPE]: j.contentType,
                [PROPS.CONTENT_ID]: j.contentId,
                [PROPS.ENDED_REASON]: ENDED_REASONS.RECOVERED,
                [PROPS.LISTENING_SECONDS_CUMULATIVE]: cumulative,
                [PROPS.ATTRIBUTION_SOURCE]: j.attributionSource
            })
        }
        clearJournal()
    }

    /**
     * Track a playback error
     */
    onPlaybackError(stage, code, message) {
        if (!this._session) return

        mixpanelService.track(EVENTS.PLAYBACK_ERROR, {
            [PROPS.PLAYBACK_REQUEST_ID]: this._session.playbackRequestId,
            [PROPS.CONTENT_TYPE]: this._session.contentType,
            [PROPS.CONTENT_ID]: this._session.contentId,
            [PROPS.ERROR_STAGE]: stage,
            [PROPS.ERROR_CODE]: code,
            [PROPS.ERROR_MESSAGE]: message,
            [PROPS.ATTRIBUTION_SOURCE]: this._session.attributionSource
        })
    }

    // Helper to accumulate time if currently playing
    _accumulateCurrentTime() {
        if (this._session?.playerState === PLAYER_STATES.PLAYING) {
            const now = performance.now()
            this._session.accumulatedListeningMs += now - this._lastStateChangeTime
            this._lastStateChangeTime = now
        }
    }

    getCurrentSession() {
        return this._session
    }

    _resetForTesting() {
        this._session = null
        this._lastStateChangeTime = null
        this._lastJournalWriteAt = 0
    }
}

export default new PlaybackTracker()
